import { useNavigate } from "flemo";
import { BrainIcon, ChevronRightIcon, ClockIcon, LockIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { PaywallDialog } from "@/components/PaywallDialog";
import { useIsPlusQuery, useReviewSummaryQuery } from "@/hooks/queries";
import { trackEvent } from "@/lib/analytics";

function daysUntil(dateStr: string): number {
  const now = new Date();
  const target = new Date(dateStr);
  const diffMs = target.getTime() - now.getTime();

  return Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

export function SmartReviewCard() {
  const { push } = useNavigate();
  const { data: isPlus = false } = useIsPlusQuery();
  const { data: summary, isError } = useReviewSummaryQuery();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const impressionTracked = useRef(false);

  const isVisible = Boolean(summary && (summary.reviewCount > 0 || summary.nextReviewAt));
  const hasReviews = (summary?.reviewCount ?? 0) > 0;
  const hasCompletedToday = (summary?.completedTodayCount ?? 0) > 0;
  const nextDays = summary?.nextReviewAt ? daysUntil(summary.nextReviewAt) : null;

  useEffect(() => {
    if (isError) {
      void trackEvent("smart_review_summary_failed");
    }
  }, [isError]);

  useEffect(() => {
    if (!isVisible || impressionTracked.current) {
      return;
    }

    impressionTracked.current = true;
    void trackEvent("smart_review_card_impression", {
      has_reviews: hasReviews,
      is_plus: isPlus,
      review_count: summary?.reviewCount ?? 0,
    });
  }, [hasReviews, isPlus, isVisible, summary?.reviewCount]);

  function handleClick() {
    trackEvent("smart_review_card_clicked", {
      has_reviews: hasReviews,
      is_plus: isPlus,
    });

    if (!isPlus) {
      trackEvent("paywall_triggered", { source: "smart_review" });
      setPaywallOpen(true);

      return;
    }

    push("/smart-review", {});
  }

  const cardContent = (() => {
    if (!isVisible) {
      return null;
    }

    if (hasReviews) {
      return (
        <>
          <button
            type="button"
            onClick={handleClick}
            className="flex w-full items-center gap-3.5 rounded-xl bg-card p-4 text-left transition-transform active:scale-[0.98]"
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/10">
              <BrainIcon className="size-5 text-violet-500" />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-semibold text-foreground truncate">오늘의 스마트 복습</p>
                <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-violet-500 text-[11px] font-bold text-white leading-none">
                  {summary!.reviewCount}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {hasCompletedToday
                  ? `복습할 단어 ${summary!.reviewCount}개 남았어요`
                  : `복습할 단어 ${summary!.reviewCount}개가 준비됐어요`}
              </p>
            </div>

            {isPlus ? (
              <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
            ) : (
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[10px] font-semibold text-violet-500">Plus</span>
                <LockIcon className="size-3.5 text-violet-500" />
              </div>
            )}
          </button>

          <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} source="smart_review" />
        </>
      );
    }

    if (nextDays !== null) {
      return (
        <button
          type="button"
          onClick={handleClick}
          className="flex w-full items-center gap-3.5 rounded-xl bg-card p-4 text-left transition-transform active:scale-[0.98]"
        >
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-slate-400/10">
            <ClockIcon className="size-5 text-slate-400" />
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="text-sm font-semibold text-foreground truncate">다음 복습</p>
            <p className="text-xs text-muted-foreground">
              {nextDays === 1 ? "내일 복습할 단어가 있어요" : `${nextDays}일 후 복습이 예정돼 있어요`}
            </p>
          </div>

          <ChevronRightIcon className="size-4 shrink-0 text-slate-300" />
        </button>
      );
    }

    return null;
  })();

  return (
    <div
      className="grid transition-[grid-template-rows,margin] duration-300 ease-out"
      style={{
        gridTemplateRows: cardContent ? "1fr" : "0fr",
        marginTop: cardContent ? 0 : -16,
      }}
    >
      <div className="overflow-hidden">{cardContent}</div>
    </div>
  );
}
